import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";
import { Trash, UploadSimple, ArrowClockwise, Crop } from "@phosphor-icons/react";
import ImageEditorModal from "./ImageEditorModal";

export default function GalleryTab() {
    const [items, setItems] = useState([]);
    const [caption, setCaption] = useState("");
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState("");
    const [editor, setEditor] = useState(null);
    const fileRef = useRef(null);

    const load = () =>
        api.get("/content")
            .then((r) => setItems(r.data.gallery || []))
            .catch(() => toast.error("Gagal memuat data"));

    useEffect(() => {
        load();
    }, []);

    const uploadMany = async (files) => {
        if (!files.length) return;
        setUploading(true);
        let ok = 0;
        for (let i = 0; i < files.length; i++) {
            setProgress(`Mengunggah ${i + 1}/${files.length}...`);
            try {
                const fd = new FormData();
                fd.append("file", files[i]);
                const r = await api.post("/admin/upload", fd);
                await api.post("/admin/gallery", { image_url: r.data.url, caption });
                ok++;
            } catch {
                toast.error(`Gagal mengunggah ${files[i].name}`);
            }
        }
        if (ok) toast.success(`${ok} foto berhasil ditambahkan ke galeri`);
        setCaption("");
        setProgress("");
        if (fileRef.current) fileRef.current.value = "";
        setUploading(false);
        load();
    };

    const remove = async (item) => {
        if (!window.confirm("Hapus foto ini dari galeri?")) return;
        try {
            await api.delete(`/admin/gallery/${item.id}`);
            toast.success("Foto dihapus");
            load();
        } catch {
            toast.error("Gagal menghapus");
        }
    };

    const rotate = async (item) => {
        try {
            await api.post("/admin/rotate-image", { path: item.image_url.replace("/api/files/", ""), direction: 90 });
            toast.success("Foto diputar 90°");
            setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, _v: Date.now() } : x)));
        } catch {
            toast.error("Gagal memutar foto");
        }
    };

    const openEditor = async (item) => {
        try {
            const res = await fetch(fileUrl(item.image_url));
            const blob = await res.blob();
            setEditor({ src: URL.createObjectURL(blob), item });
        } catch {
            toast.error("Gagal memuat foto untuk diedit");
        }
    };

    const closeEditor = () => {
        if (editor?.src) URL.revokeObjectURL(editor.src);
        setEditor(null);
    };

    const onEditorSave = async (blob) => {
        const item = editor.item;
        closeEditor();
        try {
            const fd = new FormData();
            fd.append("file", blob, "foto.jpg");
            const r = await api.post("/admin/upload", fd);
            await api.put(`/admin/gallery/${item.id}`, { image_url: r.data.url, caption: item.caption || "" });
            toast.success("Foto galeri diperbarui");
            load();
        } catch {
            toast.error("Gagal menyimpan hasil edit");
        }
    };

    return (
        <div data-testid="galeri-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-6">Galeri Kegiatan</h2>

            <div className="border border-racing-line bg-white p-6 mb-8" data-testid="galeri-upload-box">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">
                    Keterangan Foto (opsional, berlaku untuk semua foto yang diunggah)
                </label>
                <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="mis. Latihan rutin di P5 LEM"
                    data-testid="galeri-caption-input"
                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-racing-red transition-colors mb-4"
                />
                <div className="flex flex-wrap items-center gap-4">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={uploading}
                        onChange={(e) => uploadMany(Array.from(e.target.files || []))}
                        data-testid="galeri-file-input"
                        className="text-xs text-racing-muted file:mr-3 file:bg-racing-red file:border-0 file:px-5 file:py-3 file:text-xs file:uppercase file:tracking-[0.2em] file:text-white file:cursor-pointer hover:file:bg-racing-redhover"
                    />
                    {uploading && (
                        <span className="flex items-center gap-2 text-xs text-racing-red" data-testid="galeri-upload-progress">
                            <UploadSimple size={16} weight="bold" className="animate-bounce" /> {progress}
                        </span>
                    )}
                </div>
                <p className="mt-3 text-xs text-racing-muted">Bisa pilih banyak foto sekaligus. Semua foto otomatis dikompresi agar website tetap ringan.</p>
            </div>

            {items.length === 0 ? (
                <p className="text-racing-muted" data-testid="galeri-admin-empty">Belum ada foto di galeri.</p>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-racing-line border border-racing-line">
                    {items.map((item, i) => (
                        <div key={item.id} className="relative group bg-white" data-testid={`galeri-admin-item-${i}`}>
                            <img src={`${fileUrl(item.image_url)}?v=${item._v || ""}`} alt={item.caption || "Foto galeri"} className="w-full h-40 object-cover" />
                            {item.caption && (
                                <p className="px-3 py-2 text-xs text-racing-muted truncate">{item.caption}</p>
                            )}
                            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => openEditor(item)}
                                    data-testid={`galeri-edit-${i}`}
                                    className="p-2 bg-white/90 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"
                                    aria-label="Edit foto"
                                    title="Edit (crop, zoom, rotate)"
                                >
                                    <Crop size={16} />
                                </button>
                                <button
                                    onClick={() => rotate(item)}
                                    data-testid={`galeri-rotate-${i}`}
                                    className="p-2 bg-white/90 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 transition-colors"
                                    aria-label="Putar 90 derajat"
                                    title="Putar 90°"
                                >
                                    <ArrowClockwise size={16} />
                                </button>
                                <button
                                    onClick={() => remove(item)}
                                    data-testid={`galeri-delete-${i}`}
                                    className="p-2 bg-white/90 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"
                                    aria-label="Hapus"
                                >
                                    <Trash size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ImageEditorModal
                open={!!editor}
                imageSrc={editor?.src}
                aspect={1 / 1}
                title="Edit Foto Galeri"
                onCancel={closeEditor}
                onSave={onEditorSave}
            />
        </div>
    );
}
